import type { TableEditResult } from '../schemas'
import { Tooltip, useClickInsteadOfHover } from '@/components/ui/tooltip/Tooltip'
import ColumnForm from './ColumnForm'
import AddColumn from '@/assets/icons/table-add-column.svg?react'
import { Plus } from '@/assets/icons'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'
import { useState } from 'react'
import { Button } from '@/components/button/Button'
import type { TableEdit } from '../schemas'

export default function AddColumnControl({
  documentBased,
  saving,
  loading,
  inTable = false,
  limitMessage,
  onSave,
}: {
  documentBased: boolean
  saving: boolean
  loading: boolean
  inTable?: boolean
  limitMessage?: string
  onSave: (edit: TableEdit) => Promise<TableEditResult>
}) {
  const [addingColumn, setAddingColumn] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const touchInput = useClickInsteadOfHover()
  return (
    <Popover
      open={addingColumn}
      onOpenChange={(open) => {
        if (!saving) {
          setError(null)
          setAddingColumn(open)
        }
      }}
    >
      <Tooltip title={limitMessage ?? (touchInput || inTable ? '' : 'Add Column')} disableInteractive>
        <span tabIndex={limitMessage ? 0 : undefined}>
          <PopoverTrigger asChild>
            {inTable ? (
              <Button size="sm" variant="plain" color="primary" leadingIcon={<Plus />} disabled={saving || Boolean(limitMessage)}>
                Add Column
              </Button>
            ) : (
              <Button
                size="xs"
                variant="tonal"
                color="neutral"
                leadingIcon={<AddColumn className="size-4" />}
                aria-label="Add Column"
                disabled={saving || Boolean(limitMessage)}
                className="!gap-1 !px-2 disabled:!ring-0"
              >
                <span className="hidden [@container_(min-width:_640px)]:inline">Add Column</span>
              </Button>
            )}
          </PopoverTrigger>
        </span>
      </Tooltip>
      <PopoverContent
        align={inTable ? 'start' : 'end'}
        width="auto"
        className="w-[440px] max-w-[calc(100vw-32px)] max-h-[min(calc(100dvh-32px),var(--radix-popover-content-available-height))] overflow-y-auto p-4"
        aria-label="Add column"
        showArrow={false}
        collisionPadding={16}
      >
        {addingColumn && (
          <ColumnForm
            error={error ?? limitMessage}
            documentBased={documentBased}
            saving={saving}
            loading={loading}
            onSave={async (request) => {
              setError(null)
              const result = await onSave(request)
              if (!result.ok) setError(result.error)
              return result
            }}
            onClose={() => {
              setError(null)
              setAddingColumn(false)
            }}
          />
        )}
      </PopoverContent>
    </Popover>
  )
}
